import {
  Alert,
  Card,
  Checkbox,
  Choice,
  Column,
  DataStatus,
  Label,
  Separator,
  Table,
  TableRow,
} from 'platform/components';
import {Box, Grid, Heading, Hide, HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import {useFormState, useWatch} from 'react-hook-form';

import {indexBy, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetRentalPriceGroupQuery, useListRentalPriceGroupQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, useTenant, useVatRatesOptions} from '@dms/shared';

import {useNavigate} from 'shared/web';

import {RentalVehicle} from '../../core/types/RentalVehicle';
import {useVehicleFormContext} from '../../kit/hooks/useVehicleFormContext';

export function PriceGroupCard() {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();
  const navigate = useNavigate();

  const {control, formApi} = useVehicleFormContext<RentalVehicle>();

  const {errors} = useFormState({control});

  const priceGroup = useWatch({control, name: 'rentalProperties.rentalPriceGroup'});

  const {data: rentalPriceGroups, isLoading, isError} = useListRentalPriceGroupQuery({});

  const {
    data: priceGroupDetail,
    isLoading: isLoadingPriceGroupDetail,
    isError: isPriceGroupDetailError,
    isFetching: isFetchingPriceGroupDetail,
  } = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: priceGroup?.rentalPriceGroupId ?? ''},
    {skip: isNil(priceGroup?.rentalPriceGroupId)}
  );

  const [vatRates] = useVatRatesOptions();
  const vatRatesByValue = indexBy((item) => item.value, vatRates);

  const priceGroupOptions =
    rentalPriceGroups?.map((priceGroup) => ({
      value: priceGroup.rentalPriceGroupId,
      label: priceGroup.name,
      fieldLabel: priceGroup.vatType,
    })) ?? [];

  const handleCheckRow = (priceGroupItemId: string) => {
    formApi.clearErrors('rentalProperties.rentalPriceGroup.rentalPriceGroupItemId');
    formApi.clearErrors('rentalProperties.rentalPriceGroup.rentalPriceGroupId');
    formApi.clearErrors('rentalProperties.rentalPriceGroup');

    formApi.setValue('rentalProperties.rentalPriceGroup.rentalPriceGroupItemId', priceGroupItemId);
  };

  const isFormError = isNotNil(errors?.rentalProperties?.rentalPriceGroup);

  const errorMessage =
    errors?.rentalProperties?.rentalPriceGroup?.message ??
    errors?.rentalProperties?.rentalPriceGroup?.rentalPriceGroupId?.message ??
    errors?.rentalProperties?.rentalPriceGroup?.rentalPriceGroupItemId?.message;

  return (
    <Card
      title={i18n.t('entity.rental.labels.priceGroup')}
      variant="inlineGrey"
      actions={[
        {
          type: 'button',
          title: i18n.t('general.labels.settings'),
          onClick: () => navigate(settingsRoutes.priceGroupList),
          variant: 'link',
          leftIcon: 'action/settings',
        },
      ]}
    >
      <VStack spacing={4}>
        <DataStatus isLoading={isLoading} isError={isError}>
          <Grid columns={4} align="center">
            <VStack>
              <Choice
                data-testid="rentalProperties.rentalPriceGroup.rentalPriceGroupId-vehicle-form-choice"
                label={i18n.t('entity.rental.labels.name')}
                value={priceGroup?.rentalPriceGroupId}
                options={priceGroupOptions}
                formatOptionLabel={(option, meta) => {
                  const vatLabel = vatRatesByValue[option.fieldLabel ?? '']?.label;

                  if (meta.context === 'value') {
                    return option.label;
                  }

                  return (
                    <HStack spacing={2} align="baseline">
                      <Text size="small">{option.label}</Text>

                      <Show when={isNotNilOrEmpty(vatLabel)}>
                        <Text color="secondary" size="xSmall">
                          {vatLabel}
                        </Text>
                      </Show>
                    </HStack>
                  );
                }}
                onChange={(value) => {
                  if (isNil(value)) {
                    formApi.setValue('rentalProperties.rentalPriceGroup', null);
                    return;
                  }

                  formApi.setValue('rentalProperties.rentalPriceGroup.rentalPriceGroupId', value);
                }}
              />
            </VStack>

            <VStack>
              <Label>{i18n.t('general.labels.vat')}</Label>
              <Heading size={5} alternative>
                {isNotNil(priceGroup?.rentalPriceGroupId)
                  ? (vatRatesByValue[priceGroupDetail?.vatType ?? '']?.label ?? EMPTY_PLACEHOLDER)
                  : EMPTY_PLACEHOLDER}
              </Heading>
            </VStack>
          </Grid>

          <Show when={isFormError}>
            <Alert variant="error" title={errorMessage} />
          </Show>

          <DataStatus
            spacing={10}
            isEmpty={isNilOrEmpty(priceGroup) || isNilOrEmpty(priceGroupDetail?.items)}
            isLoading={isLoadingPriceGroupDetail || isFetchingPriceGroupDetail}
            isError={isPriceGroupDetailError}
            emptyMessage={
              isNilOrEmpty(priceGroupDetail?.items) && isNotNilOrEmpty(priceGroup)
                ? i18n.t('entity.rental.labels.priceGroupWithoutItems')
                : i18n.t('entity.rental.labels.notAssignedPriceGroup')
            }
          >
            <Separator spacing={0} />

            <Table
              columns={getPriceListColumns(formatCurrencySymbol(tenantCurrency))}
              variant="bordered"
            >
              {priceGroupDetail?.items.map((item) => (
                <TableRow
                  key={item.rentalPriceGroupItemId}
                  actionsWidth="5%"
                  onRowClick={() => handleCheckRow(item.rentalPriceGroupItemId)}
                >
                  <Box padding={2}>
                    <HStack spacing={4} align="center">
                      <Checkbox
                        value={priceGroup?.rentalPriceGroupItemId === item.rentalPriceGroupItemId}
                        isInvalid={isFormError}
                      />
                      <Text alternative size="small">
                        {item.name || EMPTY_PLACEHOLDER}
                      </Text>
                    </HStack>
                  </Box>
                  <Box padding={2}>
                    <Text size="small">{formatNumber(item.range.from)}</Text>
                  </Box>
                  <Box padding={2}>
                    <Text size="small">{formatNumber(item.range.to)}</Text>
                  </Box>
                  <Box padding={2}>
                    <Text size="small">{i18n.t(`entity.rental.labels.${item.range.period}`)}</Text>
                  </Box>
                  <Box padding={2}>
                    <Text size="small">
                      {formatCurrency(parseFloat(item.price.rate), tenantCurrency)}
                    </Text>
                  </Box>
                  <Box padding={2}>
                    <Text size="small">
                      {formatCurrency(parseFloat(item.price.ratePerKilometer), tenantCurrency, 2)}
                    </Text>
                  </Box>
                  <Box padding={2}>
                    <Text size="small">{formatNumber(item.price.includedKilometers)} Km</Text>
                  </Box>
                </TableRow>
              ))}
            </Table>
          </DataStatus>
        </DataStatus>
      </VStack>
    </Card>
  );
}

const HeaderCell = (props: {isFirst?: boolean; label: string}) => (
  <Box paddingVertical={2} backgroundColor="palettes.neutral.10.100">
    <HStack>
      <Hide when={props.isFirst}>
        <Separator orientation="vertical" spacing={0} />
      </Hide>

      <Space horizontal={2} />

      <Text align="left" alternative size="small">
        {props.label}
      </Text>
    </HStack>
  </Box>
);

const getPriceListColumns = (formattedTenantCurrency: string): Column[] => [
  {
    element: <HeaderCell isFirst label={i18n.t('general.labels.name')} />,
    width: '20%',
  },
  {
    element: <HeaderCell isFirst label={i18n.t('entity.rental.labels.from')} />,
    width: '10%',
  },
  {
    element: <HeaderCell label={i18n.t('entity.rental.labels.to')} />,
    width: '10%',
  },
  {
    element: <HeaderCell label={i18n.t('entity.rental.labels.period')} />,
    width: '15%',
  },
  {
    element: <HeaderCell label={i18n.t('entity.rental.labels.rate')} />,
    width: '15%',
  },
  {
    element: (
      <HeaderCell
        label={`${formattedTenantCurrency}${i18n.t('entity.rental.labels.ratePerKilometer')}`}
      />
    ),
    width: '15%',
  },
  {
    element: <HeaderCell label={i18n.t('entity.rental.labels.includedKilometers')} />,
    width: '20%',
    colspan: 2,
  },
];
